import React from "react";
import { Box, Typography } from "@mui/material";

const SVG_WIDTH = 700;
const SVG_HEIGHT = 168;
const PADDING_X = 48;
const CHART_TOP_Y = 34;
const BASELINE_Y = 88;
const LABEL_Y = 130;
const DOT_Y = 148;

function buildTrendSeries(data) {
  return data.map((item, index) => {
    const level = Number(item.level || 0);
    if (level >= 3) return 0.9;
    if (level === 2) return 0.74;
    if (level === 1) return 0.56;
    const previousLevel = Number(data[index - 1]?.level || 0);
    const nextLevel = Number(data[index + 1]?.level || 0);
    if (previousLevel > 0 || nextLevel > 0) return 0.38;
    return 0.22;
  });
}

function buildPoints(trend, dataLength) {
  return trend.map((value, index) => {
    const x =
      PADDING_X +
      (index / Math.max(dataLength - 1, 1)) * (SVG_WIDTH - PADDING_X * 2);
    const y = BASELINE_Y - value * 42;
    return { x, y };
  });
}

function buildSmoothPath(points) {
  if (!points.length) return "";
  if (points.length === 1) return `M ${points[0].x} ${points[0].y}`;

  let path = `M ${points[0].x} ${points[0].y}`;
  for (let index = 0; index < points.length - 1; index += 1) {
    const current = points[index];
    const next = points[index + 1];
    const controlOffset = (next.x - current.x) / 2;
    path += ` C ${current.x + controlOffset} ${current.y}, ${next.x - controlOffset} ${next.y}, ${next.x} ${next.y}`;
  }
  return path;
}

function buildAreaPath(points) {
  if (!points.length) return "";
  return `${buildSmoothPath(points)} L ${points[points.length - 1].x} ${BASELINE_Y} L ${points[0].x} ${BASELINE_Y} Z`;
}

function getLevelLabel(level) {
  if (level >= 3) return "High activity";
  if (level === 2) return "Solid activity";
  if (level === 1) return "Some activity";
  return "Quiet day";
}

export default function MomentumChart({ data, compact = false }) {
  if (!data?.length) return null;

  const trend = buildTrendSeries(data);
  const points = buildPoints(trend, data.length);
  const linePath = buildSmoothPath(points);
  const areaPath = buildAreaPath(points);

  return (
    <Box>
      {compact ? (
        <>
          <Box sx={{ display: "flex", alignItems: "flex-end", gap: 0.75, height: 44 }}>
            {data.map((item) => {
              const level = Number(item.level || 0);
              const height =
                level >= 3 ? "100%" : level === 2 ? "78%" : level === 1 ? "54%" : "28%";
              return (
              <Box
                key={item.key}
                title={`${item.fullLabel}: ${item.summary}`}
                sx={{
                  flex: 1,
                  minWidth: 0,
                  height,
                  borderRadius: 999,
                  background: level > 0
                    ? "linear-gradient(180deg, #22c55e 0%, #14b8a6 100%)"
                    : "rgba(148, 163, 184, 0.22)",
                }}
              />
              );
            })}
          </Box>
          <Box sx={{ display: "flex", gap: 0.75, mt: 0.6 }}>
            {data.map((item) => (
              <Typography
                key={`${item.key}:label`}
                variant="caption"
                sx={{ flex: 1, textAlign: "center", color: "#64748b", fontWeight: 750, fontSize: "0.68rem" }}
              >
                {item.label}
              </Typography>
            ))}
          </Box>
        </>
      ) : (
        <Box sx={{ width: "100%" }}>
          <Box sx={{ display: "flex", gap: 1.5, flexWrap: "wrap", mb: 0.85 }}>
            <Box sx={{ display: "flex", alignItems: "center", gap: 0.7 }}>
              <Box sx={{ width: 22, height: 3, borderRadius: 999, backgroundColor: "#24b564" }} />
              <Typography variant="caption" sx={{ color: "#64748b", fontWeight: 700 }}>
                Higher line = more visible activity
              </Typography>
            </Box>
            <Box sx={{ display: "flex", alignItems: "center", gap: 0.7 }}>
              <Box sx={{ width: 9, height: 9, borderRadius: "50%", backgroundColor: "#20b764" }} />
              <Typography variant="caption" sx={{ color: "#64748b", fontWeight: 700 }}>
                Bottom dots = any activity that day
              </Typography>
            </Box>
          </Box>
          <svg
            viewBox={`0 0 ${SVG_WIDTH} ${SVG_HEIGHT}`}
            style={{ width: "100%", height: "auto", display: "block" }}
            preserveAspectRatio="none"
          >
            <line
              x1={PADDING_X}
              y1={BASELINE_Y}
              x2={SVG_WIDTH - PADDING_X}
              y2={BASELINE_Y}
              stroke="#e7edf5"
              strokeWidth="2"
            />
            <line
              x1={PADDING_X}
              y1={BASELINE_Y - 26}
              x2={SVG_WIDTH - PADDING_X}
              y2={BASELINE_Y - 26}
              stroke="#eef3f8"
              strokeWidth="1.5"
            />
            <line
              x1={PADDING_X}
              y1={CHART_TOP_Y}
              x2={SVG_WIDTH - PADDING_X}
              y2={CHART_TOP_Y}
              stroke="#eef3f8"
              strokeWidth="1.5"
            />
            <path d={areaPath} fill="url(#momentumAreaFill)" opacity="0.15" />
            <path
              d={linePath}
              fill="none"
              stroke="#24b564"
              strokeWidth="4"
              strokeLinejoin="round"
              strokeLinecap="round"
            />

            {data.map((item, index) => {
              const point = points[index];
              return (
                <g key={item.key}>
                  <title>{`${item.fullLabel}: ${item.summary}`}</title>
                  <circle
                    cx={point.x}
                    cy={point.y}
                    r="6.5"
                    fill={item.active ? "#ffffff" : "#e2e8f0"}
                    stroke={item.active ? "#2ab66d" : "#cbd5e1"}
                    strokeWidth="3.5"
                  />
                  <text
                    x={point.x}
                    y={LABEL_Y}
                    textAnchor="middle"
                    fill="#64748b"
                    style={{ fontSize: 12, fontWeight: 700 }}
                  >
                    {item.label}
                  </text>
                  <circle
                    cx={point.x}
                    cy={DOT_Y}
                    r="4.5"
                    fill={item.active ? "#20b764" : "#d1d7e0"}
                  />
                </g>
              );
            })}

            <defs>
              <linearGradient id="momentumAreaFill" x1="0" y1="0" x2="0" y2="1">
                <stop offset="0%" stopColor="#24b564" />
                <stop offset="100%" stopColor="#24b564" stopOpacity="0" />
              </linearGradient>
            </defs>
          </svg>
          <Box
            sx={{
              mt: 0.4,
              px: 0.2,
              display: "grid",
              gridTemplateColumns: { xs: "1fr", sm: "repeat(3, minmax(0, 1fr))" },
              gap: 0.75,
            }}
          >
            {data.slice(-3).map((item) => (
              <Box
                key={`${item.key}:summary`}
                sx={{
                  borderRadius: "14px",
                  border: "1px solid #edf2f7",
                  backgroundColor: "#fbfdff",
                  px: 1,
                  py: 0.85,
                }}
              >
                <Typography variant="caption" sx={{ color: "#64748b", fontWeight: 800 }}>
                  {item.fullLabel}
                </Typography>
                <Typography variant="body2" sx={{ mt: 0.15, color: "#172540", fontWeight: 700 }}>
                  {item.summary}
                </Typography>
                <Typography variant="caption" sx={{ color: "#8192a8" }}>
                  {getLevelLabel(item.level)}
                </Typography>
              </Box>
            ))}
          </Box>
        </Box>
      )}
    </Box>
  );
}
